/**
 * Railway LLM Service
 * Routes all LLM calls through Railway backend (Gemini API)
 * Replaces OpenRouter service for cost optimization
 */

export interface LLMResponse {
  success: boolean
  data?: any
  error?: string
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
}

export interface RailwayLLMConfig {
  baseUrl: string
  timeout: number
}

export class RailwayLLMService {
  private static instance: RailwayLLMService
  private config: RailwayLLMConfig

  constructor() {
    this.config = {
      baseUrl: process.env.REACT_APP_BACKEND_URL || 'https://daredevil666coredaomvp-production.up.railway.app',
      timeout: 15000
    }
    
    console.log('✅ Railway LLM Service initialized with backend:', this.config.baseUrl)
  }

  public static getInstance(): RailwayLLMService {
    if (!RailwayLLMService.instance) {
      RailwayLLMService.instance = new RailwayLLMService()
    }
    return RailwayLLMService.instance
  }

  /**
   * Generate chat response using Railway backend
   */
  async generateChatResponse(message: string, context?: any): Promise<LLMResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/llm/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          context,
          type: 'chat_response',
          timestamp: new Date().toISOString()
        }),
        signal: AbortSignal.timeout(this.config.timeout)
      })

      if (!response.ok) {
        throw new Error(`Railway LLM API error: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()
      return {
        success: true,
        data: data.response || data.message || data,
        usage: data.usage
      }

    } catch (error: any) {
      console.error('Railway LLM chat response failed:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Parse betting intent using Railway backend
   */
  async parseBettingIntent(message: string): Promise<LLMResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/llm/parse-betting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          type: 'betting_intent',
          timestamp: new Date().toISOString()
        }),
        signal: AbortSignal.timeout(this.config.timeout)
      })

      if (!response.ok) {
        throw new Error(`Railway LLM betting parsing error: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()
      return {
        success: true,
        data: data.response || data.message || data,
        usage: data.usage
      }

    } catch (error: any) {
      console.error('Railway LLM betting parsing failed:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Generate expert advice using Railway backend
   */
  async generateExpertAdvice(context: any): Promise<LLMResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/llm/expert-advice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          context,
          type: 'expert_advice',
          timestamp: new Date().toISOString()
        }),
        signal: AbortSignal.timeout(this.config.timeout)
      })

      if (!response.ok) {
        throw new Error(`Railway LLM expert advice error: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()
      return {
        success: true,
        data: data.response || data.message || data,
        usage: data.usage
      }

    } catch (error: any) {
      console.error('Railway LLM expert advice failed:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Parse JSON response from LLM
   * Maintains compatibility with existing code
   */
  parseJSONResponse(response: string): any {
    try {
      // Try to parse as JSON first
      if (response.trim().startsWith('{') || response.trim().startsWith('[')) {
        return JSON.parse(response)
      }
      
      // If not JSON, try to extract JSON from the response
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0])
      }
      
      // Fallback: return the response as-is
      console.warn('Could not parse JSON from response:', response)
      return { message: response }
      
    } catch (error) {
      console.error('JSON parsing failed:', error)
      return { message: response }
    }
  }

  /**
   * Health check for Railway backend
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.config.baseUrl}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      })
      return response.ok
    } catch (error) {
      console.error('Railway backend health check failed:', error)
      return false
    }
  }
}

// Export singleton instance
export const railwayLLMService = RailwayLLMService.getInstance()
